/**
 * Service for finding upstream nodes in the LoadSEER hierarchy
 */

import type { LoadSEERApiClient } from '../api/client';
import type { GridAssetNodeSummaryDto, NodeTypeDto } from '../types';
import { extractNTLabel } from '../utils/node-type-helpers';

/**
 * LRU Cache for ancestor lookups
 * Stores up to 500 entries to avoid redundant API calls
 */
class AncestorCache {
  private cache = new Map<string, GridAssetNodeSummaryDto[]>();
  private readonly maxEntries: number;

  constructor(maxEntries = 500) {
    this.maxEntries = maxEntries;
  }

  get(lsid: string): GridAssetNodeSummaryDto[] | undefined {
    const value = this.cache.get(lsid);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(lsid);
      this.cache.set(lsid, value);
    }
    return value;
  }

  set(lsid: string, ancestors: GridAssetNodeSummaryDto[]): void {
    // Remove oldest if at capacity
    if (this.cache.size >= this.maxEntries) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(lsid, ancestors);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

/**
 * Finds the first upstream node matching the specified node type
 *
 * Uses stopAtLabel optimization when the target node type has an NT_ label.
 * This reduces API response size by stopping traversal at the first node
 * with that label, then client-side filtering by exact nodeType code.
 *
 * @param lsid - Node LoadSEER ID to find ancestors for
 * @param targetNodeType - Node type to search for in ancestors
 * @param apiClient - LoadSEER API client instance
 * @param cache - Optional LRU cache for ancestor lookups
 * @returns First matching ancestor node or null if none found
 */
export async function findFirstUpstreamNodeOfType(
  lsid: string,
  targetNodeType: NodeTypeDto,
  apiClient: LoadSEERApiClient,
  cache?: AncestorCache
): Promise<GridAssetNodeSummaryDto | null> {
  // Check cache first
  let ancestors: GridAssetNodeSummaryDto[] | undefined;
  if (cache) {
    ancestors = cache.get(lsid);
  }

  // Fetch if not cached
  if (!ancestors) {
    const ntLabel = extractNTLabel(targetNodeType);
    const result = await apiClient.getAncestors({
      lsid,
      stopAtLabel: ntLabel ?? undefined,
      page: 1,
      pageSize: 100,
    });

    ancestors = result.results ?? [];

    // Cache the result
    if (cache) {
      cache.set(lsid, ancestors);
    }
  }

  // Find first ancestor matching exact node type
  // Ancestors are ordered from immediate parent to root
  return ancestors.find(node => node.nodeType === targetNodeType.code) ?? null;
}

/**
 * Result of batch upstream node enrichment
 */
export interface UpstreamEnrichmentResult<T> {
  /** Items successfully enriched with upstream node info */
  enriched: Array<T & UpstreamNodeInfo>;
  /** Items that failed (null lsid, no matching ancestor, API error) */
  failed: T[];
  /** Summary statistics */
  stats: {
    total: number;
    succeeded: number;
    failed: number;
    cached: number;
  };
}

/**
 * Upstream node information added to enriched items
 */
export interface UpstreamNodeInfo {
  upstreamNodeLsid: string;
  upstreamNodeName: string;
  upstreamNodeType: string;
}

/**
 * Progress callback for batch operations
 */
export type ProgressCallback = (completed: number, total: number) => void;

/**
 * Enriches an array of items with upstream node information
 *
 * Items must have an `lsid` property. Successfully enriched items
 * get upstreamNodeLsid, upstreamNodeName, upstreamNodeType fields.
 *
 * Processes up to 5 lookups in parallel to avoid overwhelming the API.
 * Uses LRU cache to avoid redundant API calls for duplicate lsids.
 *
 * @param items - Items to enrich (must have lsid property)
 * @param targetNodeType - Node type to find in ancestors
 * @param apiClient - LoadSEER API client instance
 * @param onProgress - Optional callback for progress updates
 * @returns Enrichment result with enriched/failed items and statistics
 */
export async function enrichWithUpstreamNodes<T extends { lsid: string | null }>(
  items: T[],
  targetNodeType: NodeTypeDto,
  apiClient: LoadSEERApiClient,
  onProgress?: ProgressCallback
): Promise<UpstreamEnrichmentResult<T>> {
  const cache = new AncestorCache(500);
  const enriched: Array<T & UpstreamNodeInfo> = [];
  const failed: T[] = [];
  let completed = 0;
  let cachedLookups = 0;

  // Deduplicate lsids to minimize API calls
  const uniqueLsids = new Set<string>();
  const lsidToItems = new Map<string, T[]>();

  for (const item of items) {
    if (!item.lsid) {
      failed.push(item);
      continue;
    }
    uniqueLsids.add(item.lsid);
    const existing = lsidToItems.get(item.lsid) ?? [];
    existing.push(item);
    lsidToItems.set(item.lsid, existing);
  }

  // Process with concurrency limit of 5
  const concurrencyLimit = 5;
  const lsidArray = Array.from(uniqueLsids);
  const lsidToUpstream = new Map<string, GridAssetNodeSummaryDto | null>();

  for (let i = 0; i < lsidArray.length; i += concurrencyLimit) {
    const batch = lsidArray.slice(i, i + concurrencyLimit);
    const wasCached = batch.map(lsid => cache.get(lsid) !== undefined);

    const results = await Promise.allSettled(
      batch.map(lsid => findFirstUpstreamNodeOfType(lsid, targetNodeType, apiClient, cache))
    );

    for (let j = 0; j < results.length; j++) {
      const lsid = batch[j];
      const result = results[j];
      const isCached = wasCached[j];

      if (isCached) {
        cachedLookups++;
      }

      if (result.status === 'fulfilled') {
        lsidToUpstream.set(lsid, result.value);
      } else {
        lsidToUpstream.set(lsid, null);
      }

      completed++;
      if (onProgress) {
        onProgress(completed, uniqueLsids.size);
      }
    }
  }

  // Enrich items with results
  for (const [lsid, upstreamNode] of lsidToUpstream.entries()) {
    const itemsWithLsid = lsidToItems.get(lsid) ?? [];
    for (const item of itemsWithLsid) {
      if (upstreamNode) {
        enriched.push({
          ...item,
          upstreamNodeLsid: upstreamNode.lsid,
          upstreamNodeName: upstreamNode.name,
          upstreamNodeType: upstreamNode.nodeType,
        });
      } else {
        failed.push(item);
      }
    }
  }

  return {
    enriched,
    failed,
    stats: {
      total: items.length,
      succeeded: enriched.length,
      failed: failed.length,
      cached: cachedLookups,
    },
  };
}

export { AncestorCache };
